import type Dexie from 'dexie';

/**
 * **Product migration v005 — the team profile's clean start** (B design spec §15.5, build unit
 * B-25; requirements §3.7.36 supersedes the deprecated §3.1.2).
 *
 * One change, and it is **destructive**: every `ourPlayers` row is deleted.
 *
 * ── Why a clear and not a transformation ────────────────────────────────────
 *
 * §3.1.2's deprecation note records the stakeholder's own decision: *"Existing free-text rows are
 * **replaced, not migrated** — the stakeholder chose a clean start."* There is no honest
 * transformation available. A free-text blob (*"#3 is too fast to get in behind"*) yields no jersey
 * number, no position and no rating, and a **synthesised** row would be worse than an empty one:
 * §3.7.36 makes a secondary position **permission** for Coach Claude to move a player there, so a
 * guessed roster would manufacture exactly the licence that loses a match. Deleting the rows says
 * "we don't know"; inventing them says "we do".
 *
 * ── What the user sees, stated plainly ──────────────────────────────────────
 *
 * My Team is empty on next launch. **Every free-text row is gone, with no in-app undo**, and
 * nothing warns first, because this upgrade runs when the new bundle opens the database — before
 * any surface renders. The operational consequence is therefore an instruction rather than a hope
 * (pre-mortem PM-3): **export an archive from the currently deployed bundle before deploying this
 * one, and do not deploy it on a match day.** There is no code path that recovers the rows
 * afterwards. That sentence belongs in the release note, not only here.
 *
 * ── Two Dexie facts, verified against this repo's Dexie rather than asserted ─
 *
 *   1. **`.stores({})` and omitting `stores()` are identical** — `Dexie.prototype.version()` calls
 *      `stores({})` on every version it creates, which is what `v003_match_format.ts:40-44` records
 *      in its own words: *"Dexie inherits the previous version's schema when `stores()` is
 *      omitted."* So this version changes **no store and no index on any table**, and U-0's
 *      measured index set for the tap transaction provably cannot regress. `stores()` is omitted
 *      here rather than called with `{}` so the two are not spelled differently in one chain.
 *   2. **The upgrade callback does not run on a fresh install** — `runUpgraders` takes the
 *      `oldVersion === 0` branch — so a new device is untouched. `clear()` is idempotent either
 *      way, so the question never needs an answer at runtime; both paths are tested rather than
 *      reasoned about (`fixtureMigration.test.ts`).
 *
 * ── What is deliberately absent ─────────────────────────────────────────────
 *
 * **No `reconcileNeeded` flag.** Nothing derived changes: `ourPlayers` feeds no count, no status
 * and no invariant — the B-1 cascade-negative test pins that no cascade even reaches it.
 *
 * **No table name imported from a constant.** `'ourPlayers'` is spelled out, §13.3's rule and
 * `v003`/`v004`'s practice: a migration that named a shared constant would change what it does the
 * day that constant changed. NFR-7 is forward-only — what this ran in August 2026 must still be
 * what it runs in 2028.
 */
export function v005TeamProfile(db: Dexie): void {
  db.version(5).upgrade(async (tx) => {
    await tx.table('ourPlayers').clear();
  });
}
